import { useEffect, useRef, useState } from "react";
import checkmarkIcon from "../../assets/home-page/how-it-works/checkmark-icon.svg";
import disbursementIcon from "../../assets/home-page/how-it-works/disbursement-transfer-icon.svg";
import { siteConfig } from "../../config/site";
import { useInView } from "../../hooks/useInView";

// Active card cycles: 1 → 2 → 3 → 4 → repeat.
const SEQUENCE = [0, 1, 2, 3];
// Note: Tailwind's JIT scanner needs literal class strings, so this duration
// (800ms) must stay hardcoded in sync with TRANSITION_MS below.
const TRANSITION_MS = 800;
const PAUSE_MS = 2500;
const TRANSITION_CLASS = "transition-colors duration-[800ms] ease-in-out";

const PINK_BG = ["#f3edff", "#f7f3ff", "#f3edff", "#f3edff"];
const GREEN_BG = "#176766";

function StepShell({
  number,
  microLabel,
  title,
  description,
  isActive,
  children,
  bg,
}: {
  number: string;
  microLabel: string;
  title: string;
  description: string;
  isActive: boolean;
  children: React.ReactNode;
  bg: string;
}) {
  return (
    <div
      className={`flex flex-col justify-between rounded-[32px] border border-solid p-7 sm:min-h-[439px] ${TRANSITION_CLASS}`}
      style={{
        backgroundColor: bg,
        borderColor: isActive ? "transparent" : "#e7dce8",
      }}
    >
      <div>
        <div className="flex items-center justify-between">
          <span
            className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold backdrop-blur-[2px] ${TRANSITION_CLASS}`}
            style={{
              backgroundColor: isActive ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.7)",
              color: isActive ? "#ffffff" : "#3e025c",
            }}
          >
            {number}
          </span>
          <span
            className={`text-xs font-semibold uppercase tracking-[0.3px] ${TRANSITION_CLASS}`}
            style={{ color: isActive ? "#34d399" : "rgba(87,83,78,0.7)" }}
          >
            {microLabel}
          </span>
        </div>
        <h3
          className={`pt-3 text-2xl font-bold leading-8 tracking-[-0.6px] ${TRANSITION_CLASS}`}
          style={{ color: isActive ? "#ffffff" : "#3e025c" }}
        >
          {title}
        </h3>
        <p
          className={`pt-2 text-sm font-medium leading-[22.75px] ${TRANSITION_CLASS}`}
          style={{ color: isActive ? "rgba(255,255,255,0.85)" : "#57534e" }}
        >
          {description}
        </p>
      </div>

      {children}
    </div>
  );
}

export function HowItWorks() {
  const [activeIndex, setActiveIndex] = useState(SEQUENCE[0]);
  const seqPos = useRef(0);
  const { ref: headerRef, inView: headerInView } = useInView<HTMLDivElement>();

  useEffect(() => {
    const interval = setInterval(() => {
      seqPos.current = (seqPos.current + 1) % SEQUENCE.length;
      setActiveIndex(SEQUENCE[seqPos.current]);
    }, TRANSITION_MS + PAUSE_MS);
    return () => clearInterval(interval);
  }, []);

  const active0 = activeIndex === 0;
  const active1 = activeIndex === 1;
  const active2 = activeIndex === 2;
  const active3 = activeIndex === 3;

  return (
    <section className="mx-auto flex w-full max-w-[1440px] flex-col items-center gap-[26px] px-4 pb-16 pt-8 sm:pb-24 sm:pt-10">
      {/* Header */}
      <div
        ref={headerRef}
        className={`flex max-w-[768px] flex-col items-center gap-6 text-center ${headerInView ? "in-view" : ""}`}
      >
        <span className="reveal-scroll delay-1 rounded-full bg-white/70 px-[14px] py-[6px] text-xs font-semibold uppercase tracking-[0.6px] text-[#176766] backdrop-blur-[2px]">
          How It Works
        </span>
        <h2 className="reveal-scroll delay-2 tracking-[-1.5px] text-3xl leading-tight sm:text-5xl lg:text-[60px] lg:leading-[60px]">
          <span className="font-bold text-[#3e025c]">Get your loan in </span>
          <span className="font-serif-accent italic text-[#3e025c]">
            Minutes
          </span>
        </h2>
        <p className="reveal-scroll delay-3 max-w-[672px] text-lg leading-7 text-[#57534e]">
          From checking your eligibility to receiving your funds, our digital
          process keeps borrowing clear, fast, and completely stress-free.
        </p>
      </div>

      {/* Steps */}
      <div className="grid w-full max-w-[1300px] grid-cols-1 gap-7 pt-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* Card 1 — Check Eligibility */}
        <StepShell
          number="01"
          microLabel="Instant Check"
          title="Check Eligibility"
          description="See which loan options you qualify for in seconds without affecting your credit score."
          isActive={active0}
          bg={active0 ? GREEN_BG : PINK_BG[0]}
        >
          <div className="mx-auto mt-8 w-[210px] max-w-full rounded-[24px] border border-solid border-[#e7dce8] bg-white p-[17px] shadow-[0px_1px_1px_rgba(0,0,0,0.05)]">
            <div className="flex justify-center pb-2">
              <span className="h-[6px] w-10 rounded-full bg-[#d6d3d1]" />
            </div>
            <div className="mt-2 flex items-center gap-[10px] rounded-2xl bg-[#fff9e6] p-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#e5b53b]">
                <img src={checkmarkIcon} alt="" className="h-[14px] w-[14px]" />
              </span>
              <span className="h-2 w-20 rounded-full bg-[rgba(168,162,158,0.4)]" />
            </div>
            <div className="mt-2 flex items-center gap-[10px] rounded-2xl bg-[#e9f9f0] p-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#10b981]">
                <img src={checkmarkIcon} alt="" className="h-[14px] w-[14px]" />
              </span>
              <span className="h-2 w-14 rounded-full bg-[rgba(168,162,158,0.4)]" />
            </div>
            <button
              type="button"
              className="mt-2 w-full rounded-xl bg-[#1a1a1a] py-2 text-[10px] font-semibold leading-[15px] text-white"
            >
              Qualified • $25,000
            </button>
          </div>
        </StepShell>

        {/* Card 2 — Apply Online */}
        <StepShell
          number="02"
          microLabel="100% Paperless"
          title="Apply Online"
          description="Fill out your personal details through our streamlined application in under 5 minutes."
          isActive={active1}
          bg={active1 ? GREEN_BG : PINK_BG[1]}
        >
          <div className="mx-auto mt-8 w-[210px] max-w-full rounded-[24px] border border-solid border-[#e7dce8] bg-white p-[17px] shadow-[0px_1px_1px_rgba(0,0,0,0.05)]">
            <div className="flex items-center justify-between">
              <span className="text-xs text-[#a8a29e]">Application</span>
              <span className="text-[11px] font-semibold text-[#176766]">
                80% Done
              </span>
            </div>
            <div className="mt-3 h-[6px] w-full rounded-full bg-[#f5f5f4]">
              <div className="h-full w-[80%] rounded-full bg-[#1c1917]" />
            </div>
            <div className="mt-3 flex items-center justify-between rounded-xl bg-[#fafaf9] p-2">
              <span className="text-[11px] font-medium text-[#57534e]">
                Loan Amount
              </span>
              <span className="text-[11px] font-bold text-[#1c1917]">
                $15,000
              </span>
            </div>
            <div className="mt-2 flex items-center justify-between rounded-xl bg-[#fafaf9] p-2">
              <span className="text-[11px] font-medium text-[#57534e]">
                Duration
              </span>
              <span className="text-[11px] font-bold text-[#1c1917]">
                24 Months
              </span>
            </div>
          </div>
        </StepShell>

        {/* Card 3 — Get a Decision */}
        <StepShell
          number="03"
          microLabel="Under 90 Sec"
          title="Get a Decision"
          description="Receive an instant in-principle outcome supported by real-time automated verification."
          isActive={active2}
          bg={active2 ? GREEN_BG : PINK_BG[2]}
        >
          <div
            className={`mt-8 flex flex-col gap-2 rounded-2xl border border-solid p-[17px] ${TRANSITION_CLASS}`}
            style={{
              backgroundColor: "rgba(255,255,255,0.1)",
              borderColor: active2 ? "rgba(255,255,255,0.15)" : "#e7dce8",
            }}
          >
            <div className="flex items-center gap-2">
              <span
                className={TRANSITION_CLASS}
                style={{
                  display: "block",
                  height: "8px",
                  width: "8px",
                  borderRadius: "9999px",
                  backgroundColor: active2 ? "#34d399" : "#176766",
                }}
              />
              <span
                className={`text-xs font-semibold tracking-[0.3px] ${TRANSITION_CLASS}`}
                style={{ color: active2 ? "#ffffff" : "#3e025c" }}
              >
                Instant In-Principle
              </span>
            </div>
            <p
              className={`font-serif-accent text-xs italic leading-[18px] ${TRANSITION_CLASS}`}
              style={{ color: active2 ? "rgba(255,255,255,0.85)" : "#57534e" }}
            >
              "No waiting days on hold. Automated approval took under 90
              seconds."
            </p>
          </div>
        </StepShell>

        {/* Card 4 — Get Your Funds */}
        <StepShell
          number="04"
          microLabel="Same-Day Wire"
          title="Get Your Funds"
          description="Money directly disbursed to your bank account quickly, so you can move forward immediately."
          isActive={active3}
          bg={active3 ? GREEN_BG : PINK_BG[3]}
        >
          <div
            className={`mt-8 flex items-center justify-between rounded-2xl border border-solid p-[17px] ${TRANSITION_CLASS}`}
            style={{
              backgroundColor: "rgba(255,255,255,0.1)",
              borderColor: active3 ? "rgba(255,255,255,0.1)" : "#e7dce8",
            }}
          >
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[rgba(23,103,102,0.2)]">
                <img src={disbursementIcon} alt="" className="h-5 w-5" />
              </span>
              <div>
                <p
                  className={`text-[11px] ${TRANSITION_CLASS}`}
                  style={{ color: active3 ? "rgba(255,255,255,0.7)" : "#57534e" }}
                >
                  Disbursement
                </p>
                <p
                  className={`text-xs font-bold tracking-[-0.3px] ${TRANSITION_CLASS}`}
                  style={{ color: active3 ? "#ffffff" : "#1c1917" }}
                >
                  Direct Transfer
                </p>
              </div>
            </div>
            <span className="rounded-full border border-solid border-[rgba(52,211,153,0.2)] bg-[rgba(52,211,153,0.1)] px-[11px] py-[5px] text-xs font-bold text-[#34d399]">
              Active
            </span>
          </div>
        </StepShell>
      </div>

      <a
        href={siteConfig.applyLink || undefined}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-4 rounded-full bg-[#3e025c] px-8 py-4 text-base font-semibold text-white shadow-[0px_10px_15px_-3px_rgba(0,0,0,0.1),0px_4px_6px_-4px_rgba(0,0,0,0.1)] transition-opacity hover:opacity-90"
      >
        Apply to loan
      </a>
    </section>
  );
}
